/**
 * PDF Document Slice
 * Manages PDF document dimensions, scale, and page details
 * Migrated from FileContext for better state management
 */

import { StateCreator } from 'zustand';
import { StoreState } from '../pdf-editor.store';
import { createSliceActions } from '../utils/createNamedSet';

export interface PageDetail {
  height: number;
  width: number;
}

export interface CanvasRenderDimensions {
  width: number;
  height: number;
  scale: number;
}

export interface PdfDocumentActions {
  setCanvasRenderDimensions: (dimensions: CanvasRenderDimensions) => void;
  setPdfDefaultHeight: (height: number) => void;
  setPdfIndividualPageDetails: (details: PageDetail[]) => void;
  setPageScale: (scale: number) => void;
  resetPdfDocument: () => void;
  setPdfDocumentDetails: (details: {
    totalPages: number;
    pageDetails: PageDetail[];
    defaultHeight: number;
    pageScale: number;
    canvasRenderDimensions: CanvasRenderDimensions;
  }) => void;
  setHasPageRendered: (hasPageRendered: boolean) => void;
}

export interface PdfDocumentSlice {
  canvasRenderDimensions: CanvasRenderDimensions | null;
  pdfDefaultHeight: number | null;
  pdfIndividualPageDetails: PageDetail[] | null;
  pageScale: number;
  totalPages: number;
  hasPageRendered: boolean;
  actions: PdfDocumentActions;
}

const initialState: Omit<PdfDocumentSlice, 'actions'> = {
  canvasRenderDimensions: null,
  pdfDefaultHeight: null,
  pdfIndividualPageDetails: null,
  pageScale: 1.5,
  totalPages: 0,
  hasPageRendered: false,
};

export const createPdfDocumentSlice: StateCreator<
  StoreState,
  [['zustand/immer', never], ['zustand/devtools', never]],
  [],
  PdfDocumentSlice
> = (set) => ({
  ...initialState,

  actions: createSliceActions(set, 'pdfDocument', (namedSet) => ({
    setCanvasRenderDimensions: (dimensions: CanvasRenderDimensions) =>
      namedSet((draft) => {
        draft.pdfDocument.canvasRenderDimensions = dimensions;
      }),

    setPdfDefaultHeight: (height: number) =>
      namedSet((draft) => {
        draft.pdfDocument.pdfDefaultHeight = height;
      }),

    setPdfIndividualPageDetails: (details: PageDetail[]) =>
      namedSet((draft) => {
        draft.pdfDocument.pdfIndividualPageDetails = details;
      }),

    setPageScale: (scale: number) =>
      namedSet((draft) => {
        draft.pdfDocument.pageScale = scale;
      }),

    setPdfDocumentDetails: (details: {
      totalPages: number;
      pageDetails: PageDetail[];
      defaultHeight: number;
      pageScale: number;
      canvasRenderDimensions: CanvasRenderDimensions;
    }) =>
      namedSet((draft) => {
        draft.pdfDocument.totalPages = details.totalPages;
        draft.pdfDocument.pdfIndividualPageDetails = details.pageDetails;
        draft.pdfDocument.pdfDefaultHeight = details.defaultHeight;
        draft.pdfDocument.pageScale = details.pageScale;
        draft.pdfDocument.canvasRenderDimensions =
          details.canvasRenderDimensions;
      }),

    resetPdfDocument: () =>
      namedSet((draft) => {
        draft.pdfDocument.canvasRenderDimensions =
          initialState.canvasRenderDimensions;
        draft.pdfDocument.pdfDefaultHeight = initialState.pdfDefaultHeight;
        draft.pdfDocument.pdfIndividualPageDetails =
          initialState.pdfIndividualPageDetails;
        draft.pdfDocument.pageScale = initialState.pageScale;
      }),

    setHasPageRendered: (hasPageRendered: boolean) =>
      namedSet((draft) => {
        draft.pdfDocument.hasPageRendered = hasPageRendered;
      }),
  })),
});
